import { UserProfile, BusinessRuleResult, ConnectionData } from '../types';
import { adminService } from '@/core/services/AdminService';
import { blockService } from '@/core/services/BlockService';

/**
 * Helper function to detect if we're in production environment
 */
function isProductionEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  return hostname === 'interlaced.app' || hostname === 'interlaced.lovable.app';
}

export class VisibilityRules {
  static async shouldShowProfile(viewerUser: UserProfile, profileUser: UserProfile): Promise<BusinessRuleResult> {
    // Verificar bloqueio PRIMEIRO (antes de tudo)
    const hasBlock = await blockService.isAnyBlockBetween(viewerUser.id, profileUser.id);
    if (hasBlock) {
      return {
        allowed: false,
        reason: 'Usuário bloqueado',
        metadata: { blocked: true }
      };
    }

    // Admins vê tudo
    const isAdmin = await adminService.isAdmin(viewerUser.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador vê todos os perfis',
        metadata: { adminBypass: true }
      };
    }

    // Perfis hidden não são mostrados
    if (profileUser.hidden) {
      return {
        allowed: false,
        reason: 'Perfil está oculto'
      };
    }

    // Mesma role não pode se ver
    if (viewerUser.role === profileUser.role && viewerUser.role) {
      return {
        allowed: false,
        reason: `Usuários com role '${viewerUser.role}' não podem ver outros usuários da mesma role`
      };
    }

    return {
      allowed: true,
      reason: 'Perfil pode ser visualizado'
    };
  }

  static async filterConnections(currentUser: UserProfile, connections: ConnectionData[]): Promise<BusinessRuleResult<ConnectionData[]>> {
    // Admins vê todas as conexões
    const isAdmin = await adminService.isAdmin(currentUser.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador vê todas as conexões',
        data: connections,
        metadata: { adminBypass: true, filteredCount: 0 }
      };
    }

    // Obter todos os usuários bloqueados (ambas direções)
    const blockedUserIds = await blockService.getAllBlockedUserIds(currentUser.id);

    // Filtrar conexões com usuários bloqueados
    const filteredConnections = connections.filter(connection => {
      const otherUserId = connection.user_id === currentUser.id 
        ? connection.target_id 
        : connection.user_id;
      return !blockedUserIds.includes(otherUserId);
    });

    return {
      allowed: true,
      reason: 'Conexões filtradas aplicando regras de visibilidade e bloqueios',
      data: filteredConnections,
      metadata: { 
        filteredCount: connections.length - filteredConnections.length,
        blockedCount: connections.length - filteredConnections.length
      }
    };
  }

  static async shouldShowInFeed(viewerUser: UserProfile, profileUser: UserProfile): Promise<BusinessRuleResult> {
    // Verificar bloqueio primeiro
    const hasBlock = await blockService.isAnyBlockBetween(viewerUser.id, profileUser.id);
    if (hasBlock) {
      return {
        allowed: false,
        reason: 'Usuário bloqueado',
        metadata: { blocked: true }
      };
    }

    const visibilityResult = await this.shouldShowProfile(viewerUser, profileUser);
    
    if (!visibilityResult.allowed) {
      return visibilityResult;
    }

    // Em produção, não mostrar perfis de teste
    if (isProductionEnvironment() && profileUser.username?.toLowerCase().startsWith('test-')) {
      return {
        allowed: false,
        reason: 'Perfis de teste não são exibidos em produção'
      };
    }

    // Regras adicionais para feed
    // Verificar se o perfil completou onboarding mínimo
    if (!profileUser.verification_level || profileUser.verification_level === 'none') {
      return {
        allowed: false,
        reason: 'Perfil não verificado'
      };
    }

    return {
      allowed: true,
      reason: 'Perfil pode aparecer no feed'
    };
  }

  static async shouldShowInConnections(viewerUser: UserProfile, profileUser: UserProfile & { onboarding_completed?: boolean }): Promise<BusinessRuleResult> {
    // Verificar bloqueio primeiro
    const hasBlock = await blockService.isAnyBlockBetween(viewerUser.id, profileUser.id);
    if (hasBlock) {
      return {
        allowed: false,
        reason: 'Usuário bloqueado',
        metadata: { blocked: true }
      };
    }

    // Primeiro aplicar regras básicas de visibilidade
    const basicVisibility = await this.shouldShowProfile(viewerUser, profileUser);
    
    if (!basicVisibility.allowed) {
      return basicVisibility;
    }

    // Verificar se o onboarding foi completado
    if (!profileUser.onboarding_completed) {
      return {
        allowed: false,
        reason: 'onboarding incompleto'
      };
    }

    return {
      allowed: true,
      reason: 'Perfil pode aparecer nas conexões'
    };
  }
}
